import React, { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { useSegmentRecorder } from "@/hooks/useSegmentRecorder";
import { api } from "@/lib/api";
import { Mic, MicOff, Sparkles, Lightbulb, Loader2, Square, RotateCcw, ListChecks, MessagesSquare, ChevronLeft } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1780427670049-43aa7921e3f0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

// Six fixed lifestyle discovery questions (Sales Conversion Engine brief)
export const DISCOVERY_QUESTIONS = [
  "Tell me a little about your household — who will be living in this home with you?",
  "What's inspiring you to think about a new home right now?",
  "Can you walk me through what a typical day looks like for your family?",
  "Tell me about the most important spaces you want, to keep you sane.",
  "What frustrates you in your current home?",
  "What does your ideal outdoor space look like?",
];

// Quick-pick options for Guided mode (tap to build the answer).
const GUIDED_OPTIONS = [
  ["Just the two of us", "Young kids", "Teenagers", "Planning a family", "Multi-generational", "Pets"],
  ["Outgrowing our space", "First home", "Upgrading", "Downsizing", "Growing family", "Relocating for work"],
  ["Both work full-time", "Work from home", "School runs", "Shift work", "Love entertaining", "Quiet routine"],
  ["Big kitchen", "Home office", "Media / rumpus", "Extra bathrooms", "Walk-in pantry", "Guest room", "Alfresco"],
  ["Not enough space", "Poor storage", "Small kitchen", "No home office", "Dark & cold", "Bad layout", "No parking"],
  ["Big backyard", "Low-maintenance", "Alfresco & BBQ", "Pool", "Kids' play area", "Veggie garden", "Just a courtyard"],
];

export default function Discovery() {
  const { session, next, back, mergeData, clientType } = useJourney();

  const initAnswers = () => {
    const saved = session?.data?.discovery_answers;
    if (Array.isArray(saved) && saved.length === DISCOVERY_QUESTIONS.length) return saved;
    return DISCOVERY_QUESTIONS.map(() => "");
  };

  const [mode, setMode] = useState(session?.data?.discovery_mode || null); // null | 'guided' | 'conversation'
  const [answers, setAnswers] = useState(initAnswers);
  const [activeQ, setActiveQ] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [hints, setHints] = useState({});
  const [suggesting, setSuggesting] = useState(null);

  const activeRef = useRef(0);
  useEffect(() => { activeRef.current = activeQ; }, [activeQ]);

  const persist = useCallback((list) => {
    mergeData({ discovery_answers: list });
    if (session?.id) api.patchSession(session.id, { data: { discovery_answers: list } }).catch(() => {});
  }, [mergeData, session]);

  const chooseMode = (m) => {
    setMode(m);
    mergeData({ discovery_mode: m });
    if (session?.id) api.patchSession(session.id, { data: { discovery_mode: m } }).catch(() => {});
  };

  const appendToAnswer = useCallback((idx, text) => {
    if (!text) return;
    setAnswers((prev) => {
      const list = [...prev];
      list[idx] = (list[idx] ? list[idx] + " " : "") + text.trim();
      return list;
    });
  }, []);

  const onSegment = useCallback(async (blob) => {
    try {
      const r = await api.transcribe(session.id, blob);
      if (r.text) appendToAnswer(activeRef.current, r.text);
    } catch (e) { /* silent */ }
  }, [session, appendToAnswer]);

  const { recording, start, stop } = useSegmentRecorder(onSegment);

  const startForQuestion = async (idx) => {
    setActiveQ(idx); activeRef.current = idx;
    if (recording) return;
    const ok = await start();
    if (!ok) toast.error("Microphone unavailable — type the answer instead.");
  };
  const toggleMic = async (idx) => {
    if (recording && activeQ === idx) { stop(); persist(answers); return; }
    await startForQuestion(idx);
  };

  const editAnswer = (idx, value) => setAnswers((prev) => { const l = [...prev]; l[idx] = value; return l; });

  const resetAnswer = (idx) => {
    if (recording && activeQ === idx) stop();
    setAnswers((prev) => { const l = [...prev]; l[idx] = ""; persist(l); return l; });
    setHints((h) => ({ ...h, [idx]: [] }));
  };

  const isSelected = (idx, phrase) => (answers[idx] || "").split(",").map((s) => s.trim()).includes(phrase);
  const toggleOption = (idx, phrase) => {
    setAnswers((prev) => {
      const parts = (prev[idx] || "").split(",").map((s) => s.trim()).filter(Boolean);
      const nextParts = parts.includes(phrase) ? parts.filter((p) => p !== phrase) : [...parts, phrase];
      const l = [...prev]; l[idx] = nextParts.join(", "); persist(l); return l;
    });
  };

  const suggestFor = async (idx) => {
    setSuggesting(idx);
    try {
      const ctx = `The consultant asked: "${DISCOVERY_QUESTIONS[idx]}". The customer answered: "${answers[idx] || "(no answer yet)"}".`;
      const r = await api.suggest(session.id, ctx);
      setHints((h) => ({ ...h, [idx]: r.follow_up_questions || [] }));
    } catch (e) { toast.error("Could not fetch AI ideas."); }
    finally { setSuggesting(null); }
  };

  const buildTranscript = () =>
    DISCOVERY_QUESTIONS.map((q, i) => (answers[i]?.trim() ? `Q: ${q}\nA: ${answers[i].trim()}` : null)).filter(Boolean).join("\n\n");

  const analyze = async () => {
    if (recording) stop();
    const transcript = buildTranscript();
    if (transcript.replace(/Q:.*\n?/g, "").trim().length < 15) {
      toast.error("Capture a little more first."); return;
    }
    persist(answers);
    setAnalyzing(true);
    try {
      const r = await api.analyze(session.id, transcript);
      mergeData({ life_profile: r.life_profile });
      toast.success("Life Profile ready to review together.");
      setTimeout(() => next(), 700);
    } catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed."); }
    finally { setAnalyzing(false); }
  };

  const answered = answers.filter((a) => a.trim()).length;
  const isGuided = mode === "guided";

  return (
    <StageFrame testId="stage-discovery" className="flex flex-col lg:flex-row">
      {/* Customer view */}
      <div className="relative lg:w-[42%] h-[34vh] lg:h-screen overflow-hidden flex items-center justify-center">
        <img src={BG} alt="warm interior" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[1px]" />
        <div className="relative z-10 text-center px-8">
          <Eyebrow className="text-white/80 justify-center">Life Discovery</Eyebrow>
          <h1 className="font-serif text-white text-4xl md:text-5xl font-light leading-tight mt-4 max-w-md mx-auto">
            Tell us how you<br />want to live.
          </h1>
          {mode === "conversation" && (
            <div className="mt-8 flex flex-col items-center">
              <div className="relative grid place-items-center h-20 w-20 rounded-full bg-brand text-white shadow-2xl shadow-brand/40">
                {recording && <span className="absolute inset-0 rounded-full bg-brand/50 animate-pulse-ring" />}
                {recording ? <Mic size={26} className="text-white" /> : <MicOff size={26} className="text-white/70" />}
              </div>
              <p className="mt-5 text-white/85 text-sm tracking-wide max-w-xs">
                {recording ? `Listening — Question ${activeQ + 1}` : "Answer each question below together"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Consultant panel */}
      <div className="lg:w-[58%] flex flex-col bg-surface border-l border-line px-5 md:px-8 pt-14 pb-28 lg:h-screen overflow-y-auto">
        {!mode ? (
          <div data-testid="discovery-mode-chooser" className="my-auto">
            <Eyebrow>Choose how to discover</Eyebrow>
            <h2 className="font-serif text-3xl text-forest mt-3">How would you like to do this?</h2>
            <p className="text-sm text-mutedText mt-2">Both build the same Life Profile — pick whatever feels right for this client.</p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button data-testid="discovery-mode-guided" onClick={() => chooseMode("guided")}
                className="text-left rounded-2xl border border-line bg-white/[0.03] p-6 transition hover:border-brand/50 hover:bg-white/[0.06]">
                <div className="grid place-items-center w-12 h-12 rounded-2xl bg-brand/15 text-brand"><ListChecks size={22} /></div>
                <h3 className="font-serif text-xl text-forest mt-4">Guided</h3>
                <p className="text-sm text-mutedText mt-1.5">Tap quick-pick options for each question, then add any detail. Fast and easy.</p>
              </button>
              <button data-testid="discovery-mode-conversation" onClick={() => chooseMode("conversation")}
                className="text-left rounded-2xl border border-line bg-white/[0.03] p-6 transition hover:border-brand/50 hover:bg-white/[0.06]">
                <div className="grid place-items-center w-12 h-12 rounded-2xl bg-brand/15 text-brand"><MessagesSquare size={22} /></div>
                <h3 className="font-serif text-xl text-forest mt-4">Conversation</h3>
                <p className="text-sm text-mutedText mt-1.5">Just talk — use the mic to capture the answers, or type freely as you chat.</p>
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <button data-testid="discovery-change-mode" onClick={() => chooseMode(null)}
                className="hotspot flex items-center gap-1.5 text-xs text-mutedText hover:text-forest">
                <ChevronLeft size={14} /> {isGuided ? "Guided" : "Conversation"} · switch
              </button>
              <span data-testid="discovery-progress" className="text-xs text-mutedText">{answered} / {DISCOVERY_QUESTIONS.length} answered</span>
            </div>
            <p className="mt-2 text-sm text-mutedText leading-snug">
              {isGuided ? "Tap the options that fit, then add anything extra in the box." : "Tap the mic on a question to capture the answer, or type it in."}
            </p>

            <div className="mt-6 space-y-4">
              {DISCOVERY_QUESTIONS.map((q, i) => {
                const isActive = activeQ === i;
                const isRec = recording && isActive;
                return (
                  <motion.div key={i} data-testid={`discovery-question-${i + 1}`} onClick={() => setActiveQ(i)}
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className={`rounded-2xl border p-4 transition-colors cursor-default ${isActive ? "border-brand/60 bg-brand/5" : "border-line bg-white/5"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex gap-3">
                        <span className={`grid place-items-center h-6 w-6 shrink-0 rounded-full text-xs font-semibold ${answers[i]?.trim() ? "bg-brand text-white" : "bg-white/10 text-mutedText"}`}>{i + 1}</span>
                        <p className="text-sm font-medium text-forest leading-snug">{q}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button data-testid={`discovery-reset-${i + 1}`} onClick={(e) => { e.stopPropagation(); resetAnswer(i); }}
                          disabled={!answers[i]?.trim()}
                          className="grid place-items-center h-9 w-9 rounded-full bg-white/10 text-forest hover:bg-white/20 transition disabled:opacity-30" title="Reset">
                          <RotateCcw size={14} />
                        </button>
                        {!isGuided && (
                          <button data-testid={`discovery-mic-${i + 1}`} onClick={(e) => { e.stopPropagation(); toggleMic(i); }}
                            className={`relative grid place-items-center h-9 w-9 rounded-full transition ${isRec ? "bg-brand text-white" : "bg-white/10 text-forest hover:bg-white/20"}`} title={isRec ? "Stop" : "Record"}>
                            {isRec && <span className="absolute inset-0 rounded-full bg-brand/50 animate-pulse-ring" />}
                            {isRec ? <Square size={14} /> : <Mic size={15} />}
                          </button>
                        )}
                      </div>
                    </div>

                    {isGuided && (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid={`discovery-options-${i + 1}`}>
                        {GUIDED_OPTIONS[i].map((opt, k) => {
                          const sel = isSelected(i, opt);
                          return (
                            <button key={opt} data-testid={`discovery-option-${i + 1}-${k}`}
                              onClick={(e) => { e.stopPropagation(); toggleOption(i, opt); }}
                              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${sel ? "border-brand bg-brand text-white" : "border-line text-forest/75 hover:border-brand/50"}`}>
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <textarea data-testid={`discovery-answer-${i + 1}`} value={answers[i]}
                      onChange={(e) => editAnswer(i, e.target.value)} onFocus={() => setActiveQ(i)} onBlur={() => persist(answers)}
                      placeholder={isGuided ? "Add anything extra here…" : "The answer will appear here as you talk — or type it in…"}
                      rows={2}
                      className="mt-3 w-full resize-y rounded-xl border border-line bg-black/20 p-3 text-sm leading-relaxed text-forest/90 focus:outline-none focus:ring-2 focus:ring-brand" />

                    <div className="mt-2 flex items-center justify-between">
                      <button data-testid={`discovery-ai-ideas-${i + 1}`} onClick={(e) => { e.stopPropagation(); suggestFor(i); }}
                        disabled={suggesting === i} className="hotspot flex items-center gap-1.5 text-xs text-brand hover:text-white disabled:opacity-50">
                        {suggesting === i ? <Loader2 size={12} className="animate-spin" /> : <Lightbulb size={12} />} AI follow-up ideas
                      </button>
                      {isRec && <span className="text-xs text-brand animate-pulse">● recording</span>}
                    </div>

                    <AnimatePresence>
                      {hints[i]?.length > 0 && (
                        <motion.ul initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                          className="mt-2 space-y-1.5 border-t border-line pt-2">
                          {hints[i].map((h, k) => (
                            <li key={k} className="flex gap-2 text-xs text-mutedText leading-snug">
                              <Lightbulb size={12} className="mt-0.5 shrink-0 text-brand" /><span>{h}</span>
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            <button data-testid="discovery-analyze-btn" onClick={analyze} disabled={analyzing}
              className="mt-6 flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-white text-sm font-medium shadow-lg shadow-brand/25 transition hover:bg-brand-dark disabled:opacity-50">
              {analyzing ? <><Loader2 size={16} className="animate-spin" /> Building Life Profile…</> : <><Sparkles size={16} /> Generate Life Profile</>}
            </button>
          </>
        )}
      </div>

      <ConsultantNav testId="stage-discovery" onBack={back} onNext={null} />
    </StageFrame>
  );
}
